<template>
  <section class="TaskStatsPanel">
    <div class="TaskStatsPanel__head">
      <div>
        <span class="zp-overline">{{ $t('dashboardTaskStream') }}</span>
        <h2>{{ $t('statsTaskStatus') }}</h2>
      </div>
      <div class="TaskStatsPanel__filters">
      <v-select
        hide-details
        dense
        :items="dateRanges"
        class="TaskStatsPanel__filter"
        v-model="dateRange"
        outlined
      />

      <v-select
        hide-details
        dense
        :items="users"
        class="TaskStatsPanel__filter"
        v-model="user"
        outlined
      />
      </div>
    </div>
    <div class="TaskStatsPanel__chart">
      <LineChart :source-data="stats"/>
    </div>
  </section>
</template>
<style lang="scss">
.TaskStatsPanel {
  min-width: 0;
  border: var(--zp-border-hair) solid var(--zp-border-hairline);
  border-radius: var(--zp-radius-md);
  background: var(--zp-bg-surface);

  &__head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: var(--zp-space-md);
    padding: var(--zp-space-md);
    border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);

    h2 {
      margin: var(--zp-space-2xs) 0 0;
      color: var(--zp-text-primary);
      font-family: var(--zp-font-display);
      font-size: var(--zp-text-lg);
      font-weight: 650;
      line-height: var(--zp-leading-tight);
    }
  }

  &__filters {
    display: flex;
    align-items: center;
    gap: var(--zp-space-sm);
  }

  &__filter {
    width: 200px;
    max-width: 100%;
  }

  &__chart {
    min-height: 360px;
    padding: var(--zp-space-md);

    canvas {
      max-width: 100% !important;
    }
  }
}

@media (max-width: 700px) {
  .TaskStatsPanel {
    &__head,
    &__filters {
      align-items: stretch;
      flex-direction: column;
    }

    &__filter {
      width: 100%;
    }

    &__chart {
      min-height: 320px;
      padding: var(--zp-space-sm);

      canvas {
        width: 100% !important;
      }
    }
  }
}
</style>
<script>
import axios from 'axios';
import LineChart from '@/components/LineChart.vue';

export default {
  components: { LineChart },

  props: {
    templateId: Number,
    projectId: Number,
  },

  data() {
    return {
      dateRanges: [{
        text: this.$t('pastWeek'),
        value: 'last_week',
      }, {
        text: this.$t('pastMonth'),
        value: 'last_month',
      }, {
        text: this.$t('pastYear'),
        value: 'last_year',
      }],
      users: [{
        text: this.$t('allUsers'),
        value: null,
      }],
      user: null,
      stats: null,
      dateRange: 'last_week',
    };
  },

  computed: {
    startDate() {
      const date = new Date();

      switch (this.dateRange) {
        case 'last_year':
          date.setFullYear(date.getFullYear() - 1);
          break;
        case 'last_month':
          date.setDate(date.getDate() - 30);
          break;
        case 'last_week':
        default:
          date.setDate(date.getDate() - 7);
          break;
      }

      return date.toISOString().split('T')[0];
    },
  },

  watch: {
    async startDate() {
      await this.refreshData();
    },
    async user() {
      await this.refreshData();
    },
  },

  async created() {
    await this.refreshData();

    this.users = [{
      text: this.$t('allUsers'),
      value: null,
    }, ...(await axios({
      method: 'get',
      url: `/api/project/${this.projectId}/users`,
      responseType: 'json',
    })).data.map((x) => ({
      value: x.id,
      text: x.name,
    }))];
  },

  methods: {
    async refreshData() {
      let url;

      if (this.templateId) {
        url = `/api/project/${this.projectId}/templates/${this.templateId}/stats?start=${this.startDate}`;
      } else {
        url = `/api/project/${this.projectId}/stats?start=${this.startDate}`;
      }

      if (this.user) {
        url += `&user_id=${this.user}`;
      }

      this.stats = (await axios({
        method: 'get',
        url,
        responseType: 'json',
      })).data;

      const firstPoint = this.stats[0];

      if (!firstPoint || firstPoint.date > this.startDate) {
        this.stats.unshift({
          date: this.startDate,
          count_by_status: {
            success: 0,
            failed: 0,
            stopped: 0,
          },
        });
      }

      const lastPoint = this.stats[this.stats.length - 1];

      if (lastPoint.date < new Date().toISOString().split('T')[0]) {
        this.stats.push({
          date: new Date().toISOString().split('T')[0],
          count_by_status: {
            success: 0,
            failed: 0,
            stopped: 0,
          },
        });
      }
    },
  },
};
</script>
